import React, { useCallback, useEffect, useMemo, useRef, useState, ReactElement } from 'react';
import { BreadcrumbsItem } from 'react-breadcrumbs-dynamic';
import { translate } from 'components/Translation';
import moment from 'moment';
import GridContainer from 'components/Grid/GridContainer';
import GridItem from 'components/Grid/GridItem';
import Card from 'components/Card/Card';
import CardBody from 'components/Card/CardBody';
import AlternativeSelect from 'components/AlternativeSelect/AlternativeSelect';
import ConditionalRender from 'components/ConditionalRender/ConditionalRender';
import CanPerform from 'components/CanPerform/CanPerform';
import ChartistGraph from 'react-chartist';
import Business from '@material-ui/icons/Business';
import { AccountsService } from 'services/accounts/accounts';
import { BillsService } from 'services/bills/bills';
import { hideLoadingIndicator, showLoadingIndicator } from 'redux/modules/api/loadingIndicator/actions';
import { useDispatch, useSelector } from 'react-redux';
import { finalize } from 'rxjs/operators';
import { throwError } from 'rxjs';
import { useTheme } from '@material-ui/core/styles';
import styles from 'assets/jss/material-dashboard-pro-react/views/dashboard/dashboardStyle';
import { Box, CircularProgress, makeStyles, Typography } from '@material-ui/core';
import CustomAvatar from 'components/CustomAvatar/CustomAvatar';
import {
	BodyM,
	BodyS,
	Caption,
	HeadingSBold,
	HeadingMBold,
	HeadingLBold,
	BodySBold,
} from 'components/Typography/Typography';
import { BillsHistoryForAccount } from 'utils/api/models/bills/billsHistoryForAccount';
import BillTotal from 'views/Dashboard/subcomponents/BillTotal';
import { chartOptions, diff, setChartOptions } from 'utils/chart/chart';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import ActiveServices from 'views/Dashboard/subcomponents/ActiveServices';
import { getLanguage, Language } from 'utils/language/language';
import { setToast } from 'redux/modules/toast/actions';
import ConfirmationDialog from 'components/ConfirmationDialog/ConfirmationDialog';
import { showWelcomeModalSelector, userTypeSelector } from 'redux/modules/session/selectors';
import { setShowWelcomeModal } from 'redux/modules/session/actions';
import TryAndBuy from './subcomponents/TryAndBuy';
import { IBillingAccount } from 'utils/api/models/bills/billingIds';
import { paymentTypeSelector } from 'redux/modules/paymentType/selectors';
import { useHistory, useLocation } from 'react-router-dom';
import Banner from './subcomponents/Banner';
import { setSelectedAccountDetails } from 'redux/modules/addServices/actions';
import { RiyadaCustomerIdentifier } from 'services/riyadaCustomerIdentifier';
import { remoteConfigSelector } from 'redux/modules/settings/remoteConfig/selectors';
import { isAamaliPlus, isBOMOn, fiberPro2b, isNewDashboardEnable } from 'utils/killswitch';
import {
	bannerPinkColor,
	bannerPurpleColor,
	blackColor,
	lightGreenColor,
	primaryColor,
	primarySkyColor,
	warningColor,
	whiteColor,
} from 'assets/jss/material-dashboard-pro-react';
import { ROUTES } from 'views/Microbundle/AddNewMb/constants';
import { DASHBOARD, TRY_AND_BUY_ROUTE, fbbTyeFilter, filterTypeService, numberListFilters } from './constants';
import {
	getAamaliPlusLabels,
	getBOMLabels,
	getCommonLabels,
	getFiberProLabels,
} from 'redux/modules/dynamicLabels/selectors';
import { AAMALI_PLUS_ACCOUNT_CATEGORY, FBB_ACCOUNT_CATEGORIES } from 'views/Numbers/constants';
import { numberDetailsPath } from 'utils/paths/numberDetailsPath';
import { setFilters } from 'redux/modules/numbers/actions';
import { setSessionData } from 'components/PaymentMethod/helper/session';
import { BOM_INTRO_POPUP } from 'components/PaymentMethod/constant';
import { hasPermission } from 'utils/permissions/permissions';
import { loadQualtrics } from '../../utils/qualtrics';
import CustomCircularProgress from 'components/CustomCircularProgress/CustomCircularProgress';

// @ts-ignore
const useStyles = makeStyles(styles);

interface IDashboardProps {
	location: {
		search: string;
	};
}
interface INumberDetails {
	bAccountId: string;
	upAccountId: string;
	accountNum: string;
	accountType: string;
	billingType: string;
	accountId: string;
	addBillingType: string;
}

interface ICustomerAccountNumber {
	accountCategory: string;
	totalSubscriberNumbers: number; //Total number of numbers with accountCategory as 19
	number: INumberDetails;
	btlEligible: boolean;
	bodEligible: boolean;
}

const Dashboard = ({ location }: IDashboardProps) => {
	const classes = useStyles();
	const theme = useTheme();
	const locationPath = useLocation();
	const downXs = useMediaQuery(theme.breakpoints.down('xs'));
	const [accounts, setAccounts] = useState<Array<{ id: string; name: string; documentId: number }>>([]);
	const [billsHistory, setBillsHistory] = useState<BillsHistoryForAccount | null>(null);
	const [selectedAccount, setSelectedAccount] = useState<{ id: string; name: string; documentId: number } | null>(
		null
	);
	const [billingAccounts, setBillingAccounts] = useState<IBillingAccount[]>([]);
	const [selectedBillingAccountId, setSelectedBillingAccountId] = useState<string | undefined>(undefined);
	const [loadingAccounts, setLoadingAccounts] = useState(true);
	const [loadingBills, setLoadingBills] = useState(false);
	const [isRiyadaCustomer, setIsRiyadaCustomer] = useState(false);
	const [bankPaymentPending, setIsBankPaymentPending] = useState<boolean | null>(false);
	const [aamaliPlusAccount, setAamaliPlusAccount] = useState<ICustomerAccountNumber | null>(null);
	const [btlEligible, setBtlEligible] = useState<boolean>(false);
	const [bodEligible, setBodEligible] = useState<boolean>(false);
	const showWelcomeModal = useSelector(showWelcomeModalSelector);
	const paymentType = useSelector(paymentTypeSelector);
	const remoteConfig = useSelector(remoteConfigSelector);
	const aamaliPlus = isAamaliPlus(remoteConfig);
	const isBankMuscatEnabled = isBOMOn(remoteConfig);
	const aamaliPlusLabels = useSelector(getAamaliPlusLabels);
	const commonLabels = useSelector(getCommonLabels);
	const fiberProLabels = useSelector(getFiberProLabels);
	const bomLabels = useSelector(getBOMLabels);
	const isFiberPro2b = fiberPro2b(remoteConfig); // kill switch to show fiber pro 2b changes
	const showNewDashboard = isNewDashboardEnable(remoteConfig);

	const dispatch = useDispatch();
	const language = getLanguage();
	const isMounted = useRef(true);
	const [newSubscriptionDialogState, setNewSubscriptionDialogState] = useState<{
		isOpen: boolean;
		isError: boolean;
		isTopUp: boolean;
		topUpValue: number;
		subscriptionList: string[];
		numbers: string[];
		groupName: string;
	}>({
		isOpen: false,
		isError: false,
		isTopUp: true,
		topUpValue: 0,
		subscriptionList: [],
		numbers: [],
		groupName: '',
	});

	const history = useHistory();
	const userType = useSelector(userTypeSelector);
	const currentBillData = useMemo(() => {
		if (billsHistory && selectedBillingAccountId && selectedAccount) {
			return billsHistory.billsData.find(
				(bill) => selectedBillingAccountId === bill.billingId && bill.invoiceAmountsPerMonth
			);
		}
	}, [billsHistory, selectedBillingAccountId, selectedAccount]);

	const getChartData = useCallback(() => {
		const invoices = currentBillData?.invoiceAmountsPerMonth;
		if (currentBillData && invoices) {
			if (language === Language.AR) {
				return {
					labels: invoices
						.map((amount) =>
							moment(amount.timePeriod, 'YYYY-MM')
								.locale(language)
								.format('MMM')
						)
						.reverse(),
					series: [{ data: invoices.map((amount) => amount.amount).reverse() }],
				};
			} else {
				return {
					labels: invoices.map((amount) =>
						moment(amount.timePeriod, 'YYYY-MM')
							.locale(language)
							.format('MMM')
					),
					series: [{ data: invoices.map((amount) => amount.amount) }],
				};
			}
		}
		return {};
	}, [currentBillData, language]);

	const isDataAvailable = Object.keys(getChartData()).length !== 0;

	const getBillTotalAmount = useCallback(
		(type: 'current' | 'last' | 'lastDiff') => {
			const invoices = currentBillData?.invoiceAmountsPerMonth;
			if (currentBillData && invoices) {
				const currentAmount = invoices.length > 0 ? invoices[invoices.length - 1].amount : 0;
				const lastAmount = invoices.length > 1 ? invoices[invoices.length - 2].amount : 0;
				const prevAmount = invoices.length > 2 ? invoices[invoices.length - 3].amount : 0;
				switch (type) {
					case 'current':
						return currentAmount;
					case 'last':
						return lastAmount;
					case 'lastDiff':
						return diff(lastAmount, prevAmount);
				}
			}
			return type === 'lastDiff' ? null : 0;
		},
		[currentBillData]
	);

	const fetchBillingIds = useCallback(() => {
		return BillsService.getCorpBillingIds().subscribe((response) => {
			if (response.billingIds) {
				setBillingAccounts(
					response.billingIds.map((item) => {
						return {
							name: `${item.billingAccountName} / ${item.billingId}`,
							id: item.billingId,
							accountId: item.accountId,
							billingName: item.billingAccountName,
							key: `${item.billingId}/${item.accountId}`,
							billsHistoryFetched: false,
						};
					})
				);
			}
		});
	}, []);

	const selectedBillingAccount = useMemo(() => {
		return billingAccounts.find((acc) => acc.id === selectedBillingAccountId) || null;
	}, [billingAccounts, selectedBillingAccountId]);

	const fetchBillsHistoryForBillingId = useCallback(() => {
		const dateFormat = 'YYYY-MM-DD[T]HH:mm:ss';
		if (selectedBillingAccountId && !selectedBillingAccount?.billsHistoryFetched) {
			setLoadingBills(true);
			const subscription = BillsService.getBillsHistoryForAccounts({
				billingId: selectedBillingAccountId,
				returnTotals: true,
				returnAdjustments: false,
				corpUser: true,
				fromDate: moment()
					.subtract(7, 'months')
					.format(dateFormat),
				toDate: moment().format(dateFormat),
			})
				.pipe(
					finalize(() => {
						setLoadingBills(false);
					})
				)
				.subscribe(
					(response) => {
						setBillsHistory((oldBillsHistory: BillsHistoryForAccount | null) => {
							return {
								...oldBillsHistory,
								billsData: (oldBillsHistory?.billsData || []).concat(response.billsData),
							} as BillsHistoryForAccount;
						});
						setBillingAccounts((oldBillingAccounts) => {
							return oldBillingAccounts.map((oldBillingAccount) => {
								if (selectedBillingAccountId === oldBillingAccount.id) {
									return { ...oldBillingAccount, billsHistoryFetched: true };
								} else {
									return oldBillingAccount;
								}
							});
						});
					},
					(error) => throwError(error)
				);
			return subscription;
		}
	}, [selectedBillingAccountId, selectedBillingAccount]);

	useEffect(() => {
		const billingIdsSubscription = fetchBillingIds();

		return () => {
			billingIdsSubscription.unsubscribe();
			dispatch(hideLoadingIndicator());
		};
	}, [dispatch, fetchBillingIds]);

	useEffect(() => {
		const billsHistoryForBillingIdSubscription = fetchBillsHistoryForBillingId();

		return () => {
			if (billsHistoryForBillingIdSubscription) {
				billsHistoryForBillingIdSubscription.unsubscribe();
			}
			dispatch(hideLoadingIndicator());
		};
	}, [dispatch, fetchBillsHistoryForBillingId]);

	useEffect(() => {
		AccountsService.getCorpAccountsForCurrentUser()
			.pipe(finalize(() => isMounted.current && setLoadingAccounts(false)))
			.subscribe(
				({ accounts }) => {
					if (isMounted.current) {
						setAccounts(
							(accounts || []).map((account) => ({
								id: account.accountId,
								name: account.accountName,
								documentId: account.documentId,
							}))
						);
					}
				},
				(error) => throwError(error)
			);
	}, []);

	useEffect(() => {
		if (accounts && accounts.length > 0) {
			setSelectedAccount(accounts[0]);
			dispatch(setSelectedAccountDetails(accounts[0]));
		}
	}, [accounts]);

	useEffect(() => {
		if (!loadingAccounts && !loadingBills) {
			dispatch(hideLoadingIndicator());
		} else {
			dispatch(showLoadingIndicator());
		}
	}, [loadingAccounts, loadingBills, dispatch]);

	const filteredBillingAccounts = useMemo(() => {
		return billingAccounts.filter((billingAccount) => billingAccount.accountId === selectedAccount?.id);
	}, [billingAccounts, selectedAccount]);

	useEffect(() => {
		if (filteredBillingAccounts.length > 0 && selectedBillingAccountId === undefined) {
			setSelectedBillingAccountId(filteredBillingAccounts[0].id);
		} else if (filteredBillingAccounts.length === 0) {
			setSelectedBillingAccountId(undefined);
		} else if (selectedBillingAccount && !filteredBillingAccounts.includes(selectedBillingAccount)) {
			setSelectedBillingAccountId(filteredBillingAccounts[0].id);
		}
	}, [filteredBillingAccounts, selectedBillingAccountId, selectedBillingAccount]);

	const getLabel = (action: types.redux.api.paymentType.action) => {
		switch (action) {
			case 'TOP_UP':
				return translate('top.up.successful');
			case 'ADD_ON':
				return translate('add.on.successful');
			default:
				return translate('bills.payment.confirmed');
		}
	};

	const getDescription = (action: types.redux.api.paymentType.action) => {
		switch (action) {
			case 'TOP_UP':
				return translate('top.up.successful.description');
			default:
				return translate('bills.toast.action');
		}
	};

	useEffect(() => {
		if (location.search.includes('success')) {
			const subscriptionFromLocalStorage = localStorage.getItem('pendingPayment');
			if (subscriptionFromLocalStorage) {
				history.push('/dashboard/gift-program/?result=success');
			} else {
				dispatch(
					setToast({
						error: false,
						label: getLabel(paymentType?.action),
						actionType: getDescription(paymentType?.action),
					})
				);
				setTimeout(() => {
					loadQualtrics({
						activity_type: `Payment Success - ${paymentType?.action}`,
					});
				}, 2000);
			}
		} else if (location.search.includes('failure')) {
			localStorage.removeItem('pendingPayment');
			dispatch(
				setToast({
					error: true,
					label: translate('error.invalid.request'),
					actionType: translate('error'),
				})
			);
			setTimeout(() => {
				loadQualtrics({
					activity_type: `Payment Failure - ${paymentType?.action}`,
				});
			}, 2000);
		}
	}, [location, dispatch, paymentType]);

	useEffect(() => {
		const subscriptionFromLocalStorage = localStorage.getItem('pendingPayment');
		if (location.search.includes('result') && subscriptionFromLocalStorage) {
			return;
		}
		if (showNewDashboard && process.env.NODE_ENV !== 'development') {
			window.location.href = '/alpha/dashboard' + window.location.search;
		}
		return () => {
			isMounted.current = false;
		};
	}, []);

	useEffect(() => {
		//call Riyada API
		if (selectedAccount?.documentId) {
			setLoadingAccounts(true);
			RiyadaCustomerIdentifier.isRiyadaCustomer(selectedAccount.documentId, isBankMuscatEnabled).subscribe(
				({ response }) => {
					setLoadingAccounts(false);
					setIsRiyadaCustomer(response?.data?.riyadaCustomer);
					if (response?.data?.planDetails) {
						const aamaliPlusAccount = response.data.planDetails.find(
							(account: ICustomerAccountNumber) =>
								account.accountCategory === AAMALI_PLUS_ACCOUNT_CATEGORY
						);
						const btlEligible = response.data.planDetails.find(
							(account: ICustomerAccountNumber) =>
								FBB_ACCOUNT_CATEGORIES.includes(account.accountCategory) && account.btlEligible
						);
						const bodEligible = response.data.planDetails.find(
							(account: ICustomerAccountNumber) =>
								FBB_ACCOUNT_CATEGORIES.includes(account.accountCategory) && account.bodEligible
						);
						if (aamaliPlusAccount) setAamaliPlusAccount(aamaliPlusAccount);
						if (btlEligible) setBtlEligible(true);
						if (bodEligible) setBodEligible(true);
					}
					// Update session storage to indicate that the intro popup is open
					if (response?.data?.pendingBankMuscatPayments) setSessionData(BOM_INTRO_POPUP, 1);

					setIsBankPaymentPending(response?.data?.pendingBankMuscatPayments);
				},
				(error) => {
					setLoadingAccounts(false);
					setIsBankPaymentPending(null);
				}
			);
		}
	}, [selectedAccount]);

	// it will return number route depends on totalSubscriberNumbers
	const getAamaliPlusClaimRoute = () => {
		if (aamaliPlusAccount && aamaliPlusAccount?.totalSubscriberNumbers === 1 && aamaliPlusAccount.number) {
			return `${numberDetailsPath(
				aamaliPlusAccount.number.accountNum,
				aamaliPlusAccount.number.bAccountId,
				aamaliPlusAccount.number.accountType,
				aamaliPlusAccount.number.billingType,
				aamaliPlusAccount.number.upAccountId
			)}/aamali-services?accountCategory=${aamaliPlusAccount.accountCategory}`;
		} else {
			return ROUTES.NUMBERS;
		}
	};
	{
		/* show banner only when killswitch aamaliPlus is true and customer is Riyada customer */
	}
	const isCustomerHaveActivatedRiyada = aamaliPlusAccount && aamaliPlusAccount?.totalSubscriberNumbers > 0;
	const showAamaliBanner = aamaliPlus && isRiyadaCustomer && aamaliPlusLabels.aamaliPlusBanner;

	if (showNewDashboard && locationPath.pathname === '/dashboard/summary' && process.env.NODE_ENV !== 'development') {
		return (
			<div className={classes.loadingWrapper}>
				<CustomCircularProgress text={translate('loading.it.can.take.a.while')} loading transparent />
			</div>
		);
	}
	return (
		<GridContainer justify="flex-start">
			<ConfirmationDialog
				isOpen={!!showWelcomeModal}
				hideDialog={() => dispatch(setShowWelcomeModal(false))}
				header={translate('welcome-modal.title')}
				dialogBody={
					<div>
						<BodyS color="primary-light">{translate('welcome-modal.body')}</BodyS>
					</div>
				}
				withIcon={false}
				buttons={[]}
			/>
			<ConditionalRender
				show={!loadingAccounts}
				onTrue={() => (
					<>
						<BreadcrumbsItem to="/summary">{translate('path.dashboard')}</BreadcrumbsItem>
						<ConditionalRender
							show={accounts && accounts.length > 1}
							onTrue={() => (
								<GridItem xs={12} lg={4}>
									<GridContainer justify="flex-start" className={classes.selectContainer}>
										<GridItem xs={12} sm={6} lg={12}>
											<AlternativeSelect
												type="md"
												value={
													<div className={classes.selectedItem}>
														<CustomAvatar
															accountId={selectedAccount?.id}
															alt="Company Logo"
															placeholderIcon={<Business />}
															customClasses={classes.selectedItemIcon}
														/>
														<HeadingSBold color="primary-light">
															{selectedAccount?.name ?? translate('all.accounts')}
														</HeadingSBold>
													</div>
												}
												onClick={(item: any) => {
													setSelectedAccount(item);
													dispatch(setSelectedAccountDetails(item));
												}}
												items={accounts}
												customIcons
												selectedItems={selectedAccount ? [selectedAccount.id] : []}
											/>
										</GridItem>
									</GridContainer>
								</GridItem>
							)}
						/>
						<ActiveServices
							selectedAccount={selectedAccount}
							selectedBillingAccount={selectedBillingAccount || null}
						/>
						{/* show banner only when killswitch aamaliPlus is true and customer is Riyada customer */}
						<div className={classes.bannerContainer}>
							{hasPermission(userType, 'payment:pay_bills') && isBankMuscatEnabled && (
								<div className={classes.bannerItem}>
									<Banner
										heading={
											bankPaymentPending
												? bomLabels.banner.checkStatus
												: bomLabels.banner.payWithBom
										}
										tag={translate('dashboard.try.and.buy.new.tag')}
										badgeColor={'white'}
										description={
											bankPaymentPending
												? bomLabels.banner.checkStatusDesc
												: bomLabels.banner.payBomDesc
										}
										buttonText={
											bankPaymentPending
												? bomLabels.banner.checkStatusBtn
												: bomLabels.banner.payNow
										}
										handleClick={() => {
											history.push(
												bankPaymentPending ? ROUTES.BOM_PAYMENT_STATUS : ROUTES.MY_BILLS
											);
										}}
										textColor={whiteColor}
										buttonBackground={whiteColor}
										buttonTextColor={bankPaymentPending ? warningColor[0] : lightGreenColor}
										backgroundColor={bankPaymentPending ? warningColor[0] : lightGreenColor}
										action="dashboard:bankmuskat"
										buttonStyle={'dark'}
									/>
								</div>
							)}
							{showAamaliBanner && (
								<div className={classes.bannerItem}>
									<Banner
										heading={
											isCustomerHaveActivatedRiyada
												? aamaliPlusLabels.aamaliPlusBenefits
												: aamaliPlusLabels.aamaliPlus
										}
										tag={commonLabels.tag}
										badgeColor="black"
										description={
											isCustomerHaveActivatedRiyada
												? aamaliPlusLabels.aamaliPlusBenefitsBanner
												: aamaliPlusLabels.aamaliPlusBanner
										}
										buttonText={aamaliPlusLabels.buttonText}
										handleClick={() => {
											history.push({
												pathname: `${ROUTES.ADD_SERVICES}${selectedAccount?.id}${ROUTES.ADD_NEW_MB}`,
												state: {
													origin: DASHBOARD,
												},
											});
										}}
										action="dashboard:my-aamali-plus"
										buttonStyle="light"
										buttonBackground={primaryColor[0]}
										backgroundColor={whiteColor}
										buttonTextColor={whiteColor}
										linkText={isCustomerHaveActivatedRiyada ? aamaliPlusLabels.claimBenifits : ''}
										setFilters={() =>
											dispatch(setFilters({ type: filterTypeService, value: numberListFilters }))
										}
										linkRoute={getAamaliPlusClaimRoute()}
									/>
								</div>
							)}
							{bodEligible && isFiberPro2b && (
								<div className={classes.bannerItem}>
									<Banner
										heading={fiberProLabels.bwodBannerHeading}
										tag={commonLabels.tag}
										badgeColor="white"
										description={fiberProLabels.bwodBannerDesc}
										buttonText={commonLabels.tryNow}
										handleClick={() => {
											dispatch(setFilters({ type: filterTypeService, value: fbbTyeFilter }));
											history.push({
												pathname: `${ROUTES.NUMBERS}`,
												state: {
													origin: DASHBOARD,
												},
											});
										}}
										action="dashboard:my-aamali-plus"
										backgroundColor={bannerPinkColor}
										buttonStyle="dark"
										buttonBackground={whiteColor}
										buttonTextColor={bannerPinkColor}
										textColor={whiteColor}
									/>
								</div>
							)}
							{btlEligible && isFiberPro2b && (
								<div className={classes.bannerItem}>
									<Banner
										heading={fiberProLabels.booster}
										tag={commonLabels.tag}
										badgeColor="white"
										description={fiberProLabels.btlBannerDesc}
										buttonText={commonLabels.tryNow}
										handleClick={() => {
											dispatch(setFilters({ type: filterTypeService, value: fbbTyeFilter }));
											history.push({
												pathname: `${ROUTES.NUMBERS}`,
												state: {
													origin: DASHBOARD,
												},
											});
										}}
										action="dashboard:my-aamali-plus"
										backgroundColor={bannerPurpleColor}
										buttonStyle="dark"
										buttonBackground={whiteColor}
										buttonTextColor={bannerPurpleColor}
										textColor={whiteColor}
									/>
								</div>
							)}
							<div className={classes.bannerItem}>
								<Banner
									heading={translate('dashboard.try.and.buy')}
									description={translate('dashboard.try.and.buy.description')}
									buttonText={translate('tryandbuy.tryNowButton')}
									handleClick={() => {
										history.push(TRY_AND_BUY_ROUTE);
									}}
									buttonStyle="light"
									action="dashboard:try-and-buy-try-now"
								/>
							</div>
						</div>
						<GridItem xs={12} className={classes.usageWrapper}>
							<div className={classes.chartWrapperHeader}>
								<HeadingMBold color="secondary-light">{translate('monthly.costs')}</HeadingMBold>
							</div>
							<Card className={classes.chartWrapper}>
								<CardBody>
									<div className={classes.chartHeader} id="dashboard-monthly-costs">
										<div>
											<AlternativeSelect
												label={translate('dashboard.billing.account.name')}
												value={
													<div className={classes.selectedItem}>
														<BodyM color="primary-light">
															{selectedBillingAccount
																? `${selectedBillingAccount.billingName} / ${selectedBillingAccountId}`
																: translate('dashboard.no.billing.accounts')}
														</BodyM>
													</div>
												}
												onClick={(item: any) => setSelectedBillingAccountId(item.id)}
												items={filteredBillingAccounts}
												selectedItems={
													selectedBillingAccountId ? [selectedBillingAccountId] : []
												}
											/>
										</div>
									</div>
									<div dir={'ltr'} className={classes.chartBody}>
										<ConditionalRender
											show={
												!!selectedBillingAccount && selectedBillingAccount.billsHistoryFetched
											}
											onTrue={() => (
												<div style={{ height: '300px' }}>
													<ChartistGraph
														data={getChartData()}
														type="Bar"
														options={chartOptions}
														// @ts-ignore
														listener={{
															draw: (context: any) => setChartOptions(context, downXs),
														}}
													/>
													<ConditionalRender show={!isDataAvailable}>
														<div className={classes.noDataAvailable}>
															<BodyM color="primary-light">
																{translate('dashboard.no.data.available')}
															</BodyM>
														</div>
													</ConditionalRender>
												</div>
											)}
											onFalse={() => (
												<div className={classes.chartBodyLoading}>
													{selectedBillingAccount ? <CircularProgress /> : <></>}
												</div>
											)}
										/>
									</div>
									<div className={classes.chartLegend}>
										<div>
											<div />
											<BodyS color="secondary-light" disableMargin>
												{translate('dashboard.billing.account.this.year')}
											</BodyS>
										</div>
									</div>
									<div className={classes.chartFooter}>
										<BodyS color="secondary-light">{translate('dashboard.last.days', 6)}</BodyS>
									</div>
								</CardBody>
							</Card>
							<CanPerform
								action="dashboard:info-can-be-missed"
								yes={
									<Caption color="secondary-light">
										{translate('dashboard.some.info.can.be.missed')}
									</Caption>
								}
							/>
						</GridItem>
						<BillTotal text={translate('dashboard.current.bill')} value={getBillTotalAmount('current')} />
						<BillTotal
							text={translate('dashboard.last.bill')}
							value={getBillTotalAmount('last')}
							percentValue={getBillTotalAmount('lastDiff')}
							tooltip={translate('dashboard.in.comparison.to.previous.month')}
						/>
						<GridItem xs={12}>
							<BodyS color="disabled-light">{translate('dashboard.values.refer.to.organization')}</BodyS>
						</GridItem>
					</>
				)}
			/>
		</GridContainer>
	);
};
export function CircularProgressWithLabel(props: any) {
	return (
		<ConditionalRender
			show={props.value >= 0 && props.value < 100}
			onTrue={() => (
				<Box position="relative" display="inline-flex">
					<CircularProgress variant={'indeterminate'} {...props} />
					<Box
						top={0}
						left={0}
						bottom={0}
						right={0}
						position="absolute"
						display="flex"
						alignItems="center"
						justifyContent="center"
					>
						<Typography
							variant="caption"
							component="div"
							color="textSecondary"
						>{`${props.value}%`}</Typography>
					</Box>
				</Box>
			)}
		/>
	);
}

export default Dashboard;
